import { useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { useTransition } from 'react'
import { toast } from 'sonner'
import {
  DELETE_USAGE_ALERT_CONFIG_MUTATION,
  USAGE_ALERT_CONFIG_QUERY,
} from '@/components/shared/usage-alert-card/query'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type {
  DeleteUsageAlertConfigMutation,
  DeleteUsageAlertConfigMutationVariables,
} from '@/components/shared/usage-alert-card/query.types.gen'

type Props = {
  isOpen: boolean
  onClose: () => void
  idConfig: string
}

export const DeleteUsageAlertDialog = ({
  isOpen,
  onClose,
  idConfig,
}: Props) => {
  const [isPending, startTransition] = useTransition()
  const [deleteConfig] = useMutation<
    DeleteUsageAlertConfigMutation,
    DeleteUsageAlertConfigMutationVariables
  >(DELETE_USAGE_ALERT_CONFIG_MUTATION)

  const handleDelete = () => {
    if (isPending) {
      return
    }

    startTransition(async () => {
      try {
        await deleteConfig({
          variables: { id: idConfig },
          refetchQueries: [USAGE_ALERT_CONFIG_QUERY],
          awaitRefetchQueries: true,
        })
        toast(
          <ToasterContent variant="success">
            Usage alert deleted
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to delete usage alert
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <AlertDialogContent onEscapeKeyDown={onClose}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete usage alert?</AlertDialogTitle>
          <AlertDialogDescription>
            This will stop all email notifications for this alert. You can
            recreate it later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={onClose}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            variant="destructive"
            disabled={isPending}
            onClick={handleDelete}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Delete alert
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
